import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, XCircle, Loader2 } from 'lucide-react';

import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

/**
 * ImpersonationVerify Component
 *
 * Handles the verification of admin impersonation tokens.
 * When an admin clicks "View Portal" in the admin dashboard,
 * they're redirected here with a one-time token that gets
 * exchanged for a short-lived session JWT.
 */
const ImpersonationVerify = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'error'
  const [error, setError] = useState('');
  const [studentName, setStudentName] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError('Invalid impersonation link - no token provided');
      return;
    }

    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/impersonate/verify-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });

      if (response.ok) {
        const data = await response.json();

        // Store impersonation session in localStorage
        localStorage.setItem('memberToken', data.token);
        localStorage.setItem('memberData', JSON.stringify({
          auth_type: data.auth_type,
          entity_id: data.entity_id,
          entity: data.entity,
          permissions: data.permissions,
          is_impersonation: true,
          impersonated_by: data.impersonated_by
        }));

        // Clear any existing customer data (from previous sessions)
        localStorage.removeItem('customerToken');
        localStorage.removeItem('customerData');

        setStudentName(data.entity?.name || 'Student');
        setStatus('success');

        // Redirect to dashboard after brief delay
        setTimeout(() => {
          navigate('/');
        }, 1500);
      } else {
        const errorData = await response.json();
        setStatus('error');
        setError(errorData.detail || 'Invalid or expired impersonation link');
      }
    } catch (err) {
      console.error('Impersonation verification failed:', err);
      setStatus('error');
      setError('Something went wrong. Please try again or request a new link.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6 text-center">
          {status === 'verifying' && (
            <>
              <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Loader2 className="h-8 w-8 text-purple-600 animate-spin" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Starting Admin View Session</h2>
              <p className="text-gray-600 dark:text-gray-400">Verifying your impersonation token...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Eye className="h-8 w-8 text-purple-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Viewing as {studentName}</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-2">Redirecting to member portal...</p>
              <p className="text-sm text-purple-600 dark:text-purple-400">
                You'll see a purple banner indicating admin view mode.
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Session Failed</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
              <div className="space-y-2">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Impersonation tokens expire after 5 minutes and can only be used once.
                </p>
                <Button
                  variant="outline"
                  onClick={() => window.close()}
                  className="mt-4"
                >
                  Close Tab
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ImpersonationVerify;
